import React, { useEffect, useState } from 'react';
import { Layout, message } from 'antd';
import cookie from 'js-cookie';
import { connect } from 'react-redux';
import Routes from './Routes';
import { LeftMenu, ContentHeader } from './components/layout';
import Loading from './components/loading/Loading';
import {
  getAccountInfo,
  actionGetToken,
  actionGetPermissionToken,
  actionShowLoading,
  requestLogin,
  actionShowUploadModal,
  actionLogout,
  actionChangeViewMode,
  actionGetListPermission,
} from './view/system/systemAction';
import {
  TOKEN,
  REFRESH_TOKEN,
  FIRST_REFRESH_TOKEN,
  USER_ROLES,
} from './utils/constants/config';
import ImportDataModal from './components/uploadDatasets/ImportDataModal';
import { UploadStudyDialog } from './components/UploadStudyDialog/UploadStudyDialog';
import { UploadProjectStudyModal } from './components/UploadStudyDialog/UploadProjectStudyModal';
import { startIdleLogout } from './utils/idleLogout';
import './App.scss';

const App = (props) => {
  const { uploadInfoModal = {}, currentProject = {} } = props;
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showProjectUploadModal, setShowProjectUploadModal] = useState(false);

  useEffect(() => {
    if (cookie.get(TOKEN) || cookie.get(REFRESH_TOKEN)) {
      props.getAccountInfo();
    }
    // Set manager view mode by default
    props.actionChangeViewMode(USER_ROLES.PROJECT_OWNER);
    // eslint-disable-next-line
  }, []);

  /* The "Add to Project" button used to live in the global ContentHeader.
   * It now lives inside the Data tab (Data.js).  Rather than prop-drill
   * an open-modal callback through Routes → StudyList → Data, the Data
   * tab dispatches a window CustomEvent and we open the modal here.
   * Same modal, same payload, same backend — just a different trigger
   * point. */
  useEffect(() => {
    const onOpen = () => setShowProjectUploadModal(true);
    window.addEventListener('open-project-upload-modal', onOpen);
    return () => window.removeEventListener('open-project-upload-modal', onOpen);
  }, []);

  useEffect(() => {
    if (!cookie.get(TOKEN) && !cookie.get(REFRESH_TOKEN)) {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      if (code) {
        getToken(code);
      } else if (!error) {
        requestLogin();
      } else {
        const error_description = urlParams.get('error_description');
        error_description && message.error(error_description || '');
      }
    }
  }, []);

  const getToken = async (code) => {
    try {
      props.actionShowLoading();
      console.log('GET TOKEN');
      const res = await actionGetToken(code);
      const resPermission = await actionGetListPermission(
        res?.data?.access_token
      );
      cookie.set(FIRST_REFRESH_TOKEN, res?.data?.refresh_token, {
        expires: new Date(
          (res?.data?.refresh_expires_in || 1800) * 1000 + Date.now()
        ),
      });
      let accessToken = res?.data?.access_token;
      let refreshToken = res?.data?.refresh_token;
      let expiresIn = res?.data?.expires_in || 1800;
      let refreshExpiresIn = res?.data?.refresh_expires_in || 1800;

      try {
        const { data } = await actionGetPermissionToken(
          res?.data?.access_token,
          resPermission?.data?.data
        );
        accessToken = data?.access_token || accessToken;
        refreshToken = data?.refresh_token || refreshToken;
        expiresIn = data?.expires_in || expiresIn;
        refreshExpiresIn = data?.refresh_expires_in || refreshExpiresIn;
      } catch (err) {
        console.warn('UMA permission token failed, using access token', err);
      }

      cookie.set(TOKEN, accessToken, {
        expires: new Date(expiresIn * 1000 + Date.now()),
      });
      cookie.set(REFRESH_TOKEN, refreshToken, {
        expires: new Date(refreshExpiresIn * 1000 + Date.now()),
      });

      cookie.remove(FIRST_REFRESH_TOKEN);
      props.getAccountInfo();
    } catch (error) {
      console.log(error);
      setTimeout(() => {
        actionLogout();
      }, 1000);
      message.error('System error!');
    }
  };

  if (!cookie.get(TOKEN) && !cookie.get(REFRESH_TOKEN)) {
    return <Loading dark />;
  }

  // Signed in: sign out after 30 minutes without input (INSA finding 4).
  startIdleLogout();

  return (
    <div className="app-container">
      <Loading dark />
      <Layout>
        <Layout>
          <LeftMenu />
          <Layout.Content className="content-container">
            <ContentHeader
              onUploadClick={() => setShowUploadModal(true)}
              onProjectUploadClick={() => setShowProjectUploadModal(true)}
            />
            <Routes />
          </Layout.Content>
        </Layout>
      </Layout>
      {uploadInfoModal.isShow && (
        <ImportDataModal
          projectId={uploadInfoModal.projectId}
          onCancel={() => {
            props.actionShowUploadModal({
              isShow: false,
              projectId: '',
              studyParams: {},
            });
          }}
        />
      )}
      <UploadStudyDialog
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => {
          setShowUploadModal(false);
          // Notify any listening pages (e.g. All Studies) to refresh their list
          // without requiring a page reload. Studies.jsx subscribes to this event.
          try {
            window.dispatchEvent(new CustomEvent('study-uploaded'));
          } catch (e) {
            // CustomEvent ctor unavailable in very old browsers — safe to ignore.
          }
        }}
      />
      <UploadProjectStudyModal
        open={showProjectUploadModal}
        onClose={() => setShowProjectUploadModal(false)}
        onSuccess={() => setShowProjectUploadModal(false)}
        projectId={currentProject?.id}
        projectName={currentProject?.name}
      />
    </div>
  );
};

export default connect(
  (state) => ({
    uploadInfoModal: state.system.uploadInfoModal,
    currentProject: state.project.currentProject,
  }),
  { getAccountInfo, actionShowLoading, actionShowUploadModal, actionChangeViewMode }
)(App);
